import { useState, useEffect, useRef, useCallback } from 'react';

const MODES = {
  SYSTEM: 'system',
  VITALITY: 'vitality',
  ZEN: 'zen'
};

// Frequencies
const SYSTEM_SCALE = [220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 415.30, 440.00];
const VITALITY_SCALE = [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25];
const ZEN_SCALE = SYSTEM_SCALE.map(f => f / 2);

const useAmbientMusic = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMode, setCurrentMode] = useState(MODES.SYSTEM);
  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const delayNodeRef = useRef(null);
  const filterRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Use a ref for the mode to avoid stale closures in the generative engine
  const modeRef = useRef(currentMode);
  useEffect(() => {
    modeRef.current = currentMode;
  }, [currentMode]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const stopMusic = useCallback(() => {
    clearAllTimeouts();
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(console.error);
      audioCtxRef.current = null;
    }
    setIsPlaying(false);
  }, [clearAllTimeouts]);

  const playPad = useCallback((freq, time, duration, volume = 0.04) => {
    if (!audioCtxRef.current || !filterRef.current) return;
    const ctx = audioCtxRef.current;

    const mode = modeRef.current;
    const oscCount = 3;
    for (let i = 0; i < oscCount; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      // Detune slightly for "ethereal" chorus effect
      osc.frequency.setValueAtTime(freq + (i - 1) * 1.2, time);

      gain.gain.setValueAtTime(0, time);
      // Zen mode has longer attacks
      const attackRatio = mode === MODES.ZEN ? 0.8 : 0.4;
      gain.gain.linearRampToValueAtTime(volume / oscCount, time + duration * attackRatio);
      gain.gain.linearRampToValueAtTime(0, time + duration);

      osc.connect(gain);
      gain.connect(filterRef.current);

      osc.start(time);
      osc.stop(time + duration);
    }
  }, []);

  const playMelodyNote = useCallback((freq, time, duration, volume = 0.05) => {
    if (!audioCtxRef.current || !delayNodeRef.current || !masterGainRef.current) return;
    const ctx = audioCtxRef.current;

    const mode = modeRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = mode === MODES.VITALITY ? 'sine' : 'triangle';
    osc.frequency.setValueAtTime(freq, time);

    // ADSR: Soft attack and long release
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(volume, time + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

    osc.connect(gain);
    gain.connect(masterGainRef.current);
    gain.connect(delayNodeRef.current);

    osc.start(time);
    osc.stop(time + duration);
  }, []);

  const startGenerativeEngine = useCallback(() => {
    if (!audioCtxRef.current) return;

    const mode = modeRef.current;
    let activeScale = SYSTEM_SCALE;
    if (mode === MODES.VITALITY) activeScale = VITALITY_SCALE;
    if (mode === MODES.ZEN) activeScale = ZEN_SCALE;

    const schedulePads = () => {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') return;
      const now = audioCtxRef.current.currentTime;

      const chords = [
        [activeScale[0]/2, activeScale[2]/2, activeScale[4]/2],
        [activeScale[3]/2, activeScale[5]/2, activeScale[0]],
        [activeScale[4]/2, activeScale[6]/2, activeScale[1]],
        [activeScale[5]/2, activeScale[0], activeScale[2]]
      ];

      const chord = chords[Math.floor(Math.random() * chords.length)];
      chord.forEach(freq => playPad(freq, now, 10));

      const nextPadTimeout = setTimeout(schedulePads, 8000);
      timeoutsRef.current.push(nextPadTimeout);
    };

    const scheduleMelody = () => {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') return;

      const currentModeInEngine = modeRef.current;
      // Zen mode disables melody
      if (currentModeInEngine === MODES.ZEN) return;

      const now = audioCtxRef.current.currentTime;

      if (Math.random() > 0.3) {
        const note = activeScale[Math.floor(Math.random() * activeScale.length)];
        const octave = Math.random() > 0.7 ? 2 : 1;
        playMelodyNote(note * octave, now, 3 + Math.random() * 3);
      }

      // Vitality mode is more frequent
      const baseDelay = currentModeInEngine === MODES.VITALITY ? 1500 : 3000;
      const randomDelay = currentModeInEngine === MODES.VITALITY ? 2000 : 4000;
      const nextMelodyTimeout = setTimeout(scheduleMelody, baseDelay + Math.random() * randomDelay);
      timeoutsRef.current.push(nextMelodyTimeout);
    };

    schedulePads();
    scheduleMelody();
  }, [playPad, playMelodyNote]);

  const toggleMusic = useCallback((forceStart = false) => {
    if (isPlaying && !forceStart) {
      stopMusic();
    } else {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.4, ctx.currentTime);
      masterGain.connect(ctx.destination);
      masterGainRef.current = masterGain;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(450, ctx.currentTime);
      filter.Q.setValueAtTime(1.5, ctx.currentTime);
      filter.connect(masterGain);
      filterRef.current = filter;

      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(0.08, ctx.currentTime);
      lfoGain.gain.setValueAtTime(150, ctx.currentTime);
      lfo.connect(lfoGain);
      lfoGain.connect(filter.frequency);
      lfo.start();

      const delay = ctx.createDelay();
      delay.delayTime.setValueAtTime(0.6, ctx.currentTime);
      const delayFeedback = ctx.createGain();
      delayFeedback.gain.setValueAtTime(0.45, ctx.currentTime);
      const delayFilter = ctx.createBiquadFilter();
      delayFilter.type = 'lowpass';
      delayFilter.frequency.setValueAtTime(1200, ctx.currentTime);

      delay.connect(delayFeedback);
      delayFeedback.connect(delayFilter);
      delayFilter.connect(delay);
      delay.connect(masterGain);
      delayNodeRef.current = delay;

      setIsPlaying(true);
      startGenerativeEngine();
    }
  }, [isPlaying, stopMusic, startGenerativeEngine]);

  const cycleMode = useCallback((step) => {
    const allModes = Object.values(MODES);
    const currentIndex = allModes.indexOf(currentMode);
    const nextIndex = (currentIndex + step + allModes.length) % allModes.length;
    const nextMode = allModes[nextIndex];

    setCurrentMode(nextMode);
    modeRef.current = nextMode;

    if (isPlaying) {
      stopMusic();
      // Reinicio rápido para cargar nuevas escalas
      setTimeout(() => {
        toggleMusic(true);
      }, 50);
    }
  }, [currentMode, isPlaying, stopMusic, toggleMusic]);

  useEffect(() => {
    return () => {
      clearAllTimeouts();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(console.error);
      }
    };
  }, [clearAllTimeouts]);

  return { isPlaying, toggleMusic, currentMode, cycleMode };
};

export default useAmbientMusic;
